/**
 * Serif alphabets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const serifAlphabetsPack: ToilePack = {
    slug: 'serif-alphabets',
    category: 'illustration',
    name: 'Serif alphabets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'roman', label: 'Roman capitals', subject: 'a chart of the Roman inscriptional capitals "A" to "Z" in five rows, wedge-shaped serifs and classical proportions'},
        {key: 'slab', label: 'Slab serif', subject: 'a chart of slab serif capitals "A" to "Z" and lowercase "a" to "z", thick square serifs and even stroke weight'},
        {key: 'didone', label: 'High contrast', subject: 'a chart of high-contrast serif capitals "A" to "Z" with hairline serifs and heavy vertical stems, five rows'},
    ],
    styles: [
        {key: 'solid', label: 'Solid black', style: 'solid black letterforms with crisp serifs', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'outline', label: 'Outline', style: 'the letterforms drawn as a thin outline only, white inside', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'guides', label: 'With guides', style: 'solid black letterforms standing on thin horizontal baseline, x-height and cap-height guide lines', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
    ],
    directions: ['numbers', 'a word', 'italic'],
    directionHint: 'try: numbers',
    seo: {
        title: 'Serif Alphabets: Roman, Slab, High-Contrast',
        metaDescription: 'Nine serif reference sheets: Roman capitals, slab serif and high-contrast alphabets, drawn black on white.',
        h1: 'Serif alphabets: Roman capitals, slab serif, high-contrast serif',
        tagline: 'Three serif families on reference sheets, in solid, outline and guided forms.',
        intro: 'Serif alphabets are letters in their classic families: Roman, slab and high-contrast, for lettering, signs and engraving. The nine reference sheets chart the Roman inscriptional capitals A to Z with wedge-shaped serifs, slab serif capitals and lowercase with thick square serifs, and high-contrast capitals with hairline serifs and heavy stems. Each family is shown in solid black, as a thin outline, or with baseline guides. Ask for numbers, a word or italic.',
        useCases: [
            'a reference for a sign painter',
            'a guide for an engraving',
            'a poster heading in a classic serif',
            'a lettering study sheet',
        ],
        faq: [
            {q: 'What is the difference between slab and high-contrast?', a: 'Slab has thick, square serifs and even stroke weight. High-contrast has hairline serifs and heavy vertical stems.'},
            {q: 'What is a Roman capital?', a: 'The inscriptional capital with wedge-shaped serifs, the form of letters cut into stone.'},
            {q: 'Are these complete alphabets?', a: "Each family's capitals run from A to Z. The slab sheet also has lowercase a to z."},
            {q: 'What do the guides show?', a: 'The guided sheet shows baseline, x-height and cap-height lines, so the letters can be placed to a line.'},
        ],
    },
}
